import { Outlet, useNavigate, useParams } from 'react-router';

import { EmptyState } from '@ds/feedback';
import { Tabs } from '@ds/navigation';
import { useRotaAtual } from '@/router/useRotaAtual';
import type { Rota, Subrota } from '@/router/rotas';

export interface TelaPendenteProps {
    rota: Rota;
    /** Presente quando quem está montada é a subrota. */
    subrota?: Subrota;
}

/**
 * Marca-lugar de tela ainda não portada.
 *
 * O roteador precisa de um elemento por rota para que URL, F5, voltar/avançar,
 * 404 e as guardas possam ser exercidos antes de as telas existirem. Este
 * componente diz **qual** tela falta e **onde** está o visual dela, em vez de
 * mostrar uma página em branco que se confunde com defeito.
 */
export function TelaPendente({ rota, subrota }: TelaPendenteProps) {
    const navigate = useNavigate();
    const params = useParams();
    const atual = useRotaAtual();

    /* A subrota montada desenha só o próprio aviso: as abas são do módulo, e
       quem as desenha é o pai — repeti-las aqui daria duas fileiras iguais. */
    if (subrota) return <Aviso rota={rota} subrota={subrota} />;

    const subrotaAtiva = atual?.rota.id === rota.id ? atual.subrota : undefined;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
            {rota.children.length > 0 && (
                <Tabs
                    ariaLabel={`Seções de ${rota.label}`}
                    tabs={rota.children.map((c) => ({ id: c.path, label: c.label }))}
                    activeId={subrotaAtiva?.path}
                    onChange={(id) => navigate(`${caminhoConcreto(rota.path, params)}/${id}`)}
                />
            )}

            {!subrotaAtiva && <Aviso rota={rota} />}
            <Outlet />
        </div>
    );
}

function Aviso({ rota, subrota }: TelaPendenteProps) {
    return (
        <EmptyState
            icon={rota.icon}
            title={`${rota.screen}${subrota ? ` · ${subrota.label}` : ''} ainda não foi portada`}
            description={
                <>
                    A rota está no ar e as guardas já valem — falta a tela. O visual está em{' '}
                    <code style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12.5 }}>{rota.proto}</code>.
                </>
            }
        />
    );
}

/**
 * Troca `:contatoId` pelo id que veio na URL. Sem isso, navegar entre as abas
 * de um paciente levaria ao caminho literal, com os dois-pontos.
 */
function caminhoConcreto(padrao: string, params: Readonly<Record<string, string | undefined>>): string {
    return padrao
        .split('/')
        .map((segmento) => (segmento.startsWith(':') ? params[segmento.slice(1)] ?? segmento : segmento))
        .join('/');
}
